/**
 * relatedRail.tsx
 */
import ArrowsSelector from '../nav/arrowsSelector';
import Event from '@shared/classes/event';
import Focus from '@shared/types/enums/focus';
import MainContext from '@shared/components/utils/context/mainContext';
import Rail from '@shared/classes/rail';
import RailItemContainer from '@components/atoms/container/railItemContainer';
import RailTitle from '@components/atoms/text/railTitle';
import RegularEvent from '@shared/classes/regularEvent';
import useKeyboard from '@shared/components/utils/hooks/useKeyboard';
import useCard from '@components/sections/rails/lib/useCard';
import useStableSelectHandler from '@components/sections/rails/lib/useStableSelectHandler';
import goToContent from '@shared/actions/content/goToContent';
import { selectFocus, setFocus } from '@shared/store/slices/focus.slice';
import { selectMagicRemoteEnabled } from '@shared/store/slices/magicRemote.slice';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

const MAX_RELATED_ITEMS = 15;

/**
 * This rail is specific to the detail page.
 * It is displayed always at the bottom of the page and no other rails are displayed below it.
 */
function RelatedRail({ rail }: { rail: Rail }) {
  const dispatch = useAppDispatch();

  // Components
  const { Card } = useCard(rail);

  // Selectors
  const enabled = useAppSelector(selectMagicRemoteEnabled);
  const focusSlice = useAppSelector(selectFocus);

  // State
  const [hovered, setHovered] = useState(false);
  const [index, setIndex] = useState(0);

  // Refs
  const indexRef = useRef(index);
  const railRef = useRef(rail);
  indexRef.current = index;
  railRef.current = rail;

  /**
   * Select (or navigate to) the related asset at `index`. Reads from
   * `railRef` rather than closing over `rail`/`index` directly so this
   * callback's identity never changes, keeping `useStableSelectHandler`'s
   * per-index handler cache valid across renders.
   */
  const selectRelatedAsset = useCallback((_id: string, idx: number) => {
    const asset = railRef.current.data[idx];
    if (asset) goToContent(asset as Event);
  }, []);
  const getClickHandler = useStableSelectHandler(rail.id, selectRelatedAsset);

  /**
   * Disable hover when magic remote is disabled.
   */
  useEffect(() => {
    if (!enabled) {
      setHovered(false);
    }
  }, [enabled]);

  /**
   * Memoize callbacks and focused state.
   */
  const { focused, callbacks } = useMemo(() => {
    return {
      callbacks: {
        onMouseEnter: () => {
          dispatch(setFocus(Focus.RAILS_LIST));
          setHovered(true);
        },
        onMouseLeave: () => {
          setHovered(false);
        },
      },
      focused: !enabled && focusSlice === Focus.RAILS_LIST,
    };
  }, [enabled, focusSlice]);

  /**
   * Memoize the context for the related rail.
   */
  const context = useMemo(() => {
    return {
      changeAsset: (_id: string, index: number) => setIndex(index),
      hoverAsset: () => null,
      selectAsset: () => null,
      config: {
        display: {
          channels: false,
          description: false,
          filter: false,
          search: false,
          sliders: false,
        },
      },
      layout: {},
    };
  }, []);

  /**
   * Handle keyboard events for the related rail.
   */
  useKeyboard({
    focus: Focus.RAILS_LIST,
    keyDownHandler: (key) => {
      switch (key) {
        case 'SELECT':
          selectRelatedAsset(rail.id, indexRef.current);
          break;
        case 'LEFT':
          setIndex((prev) => Math.max(0, prev - 1));
          break;
        case 'RIGHT':
          setIndex((prev) =>
            Math.min(railRef.current.data.length - 1, prev + 1)
          );
          break;
      }
    },
  });

  /**
   * Render the related rail.
   */
  return (
    <MainContext.Provider value={context}>
      <RailItemContainer {...callbacks}>
        {rail.title ? <RailTitle>{rail.title}</RailTitle> : null}
        <div>
          {(rail.data as RegularEvent[])?.map((item, idx) => {
            if (!item) return null;

            const display = Math.abs(idx - index) < MAX_RELATED_ITEMS;
            const hideItem = index > idx;

            return display ? (
              <Card
                key={`${item?.id}-${idx}`}
                item={item}
                hide={hideItem}
                focused={focused && index === idx}
                onClick={getClickHandler(idx)}
              />
            ) : null;
          })}
        </div>
        <ArrowsSelector hovered={hovered} hIndex={index} rail={rail} />
      </RailItemContainer>
    </MainContext.Provider>
  );
}

export default memo(RelatedRail);
